import { palette } from '@innovyze/stylovyze';
import { createTheme, Grid, Input, Slider, ThemeProvider } from '@mui/material';
import * as React from 'react';

export interface SliderProps {
	invalid?: boolean;
	value: number;
	dataCy?: string;
	onSliderChanged?: (value: number) => void;
	color?: 'primary' | 'secondary';
	disabled?: boolean;
	marks?: 'off' | 'minmax' | 'minmaxticks' | '25' | '50';
	ThumbComponent?: React.ElementType<React.HTMLAttributes<HTMLSpanElement>>;
	track?: 'normal' | false | 'inverted';
	// ValueLabelComponent?: React.ElementType<ValueLabelProps>;
	valueLabelDisplay?: 'on' | 'auto' | 'off';
	padLeft?: string;
}

export const PercentSlider = ({
	invalid = false,
	dataCy,
	onSliderChanged,
	disabled,
	marks,
	value,
	valueLabelDisplay,
	padLeft,
}: SliderProps) => {
	// This needs to be done this way otherwise, for some reason, the slider stops sliding !
	const CustomSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: palette.primary.main,
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: palette.primary.main,
						color: palette.primary.main,
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						top: '23px',
						backgroundColor: 'transparent',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 2,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const InvalidSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: palette.error.dark,
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: palette.error.dark,
						color: palette.error.dark,
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						backgroundColor: 'transparent',
						top: '23px',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 1,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const DisabledSlider = createTheme({
		components: {
			MuiSlider: {
				styleOverrides: {
					root: {
						color: '#bdbdbd',
						height: 4,
					},
					thumb: {
						height: 24,
						width: 24,
						backgroundColor: '#bdbdbd',
						color: '#bdbdbd',
						border: '2px solid currentColor',
						'&:focus, &:hover, &$active': {
							boxShadow: 'inherit',
						},
					},
					markLabel: {},
					marked: {
						marginBottom: '0px',
						marginTop: '7px',
					},
					active: {},
					valueLabel: {
						backgroundColor: 'transparent',
						top: '23px',
						fontSize: '11px',
					},
					track: {
						marginTop: -1,
						height: 4,
						borderRadius: 4,
					},
					rail: {
						height: 2,
						borderRadius: 4,
					},
				},
			},
		},
	});

	const getMarks = (
		marks?: 'off' | 'minmax' | 'minmaxticks' | '25' | '50',
	) => {
		switch (marks) {
			case 'off': {
				return;
			}
			case 'minmax': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case 'minmaxticks': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 25,
					},
					{
						value: 50,
					},
					{
						value: 75,
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case '25': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 25,
						label: '25%',
					},
					{
						value: 50,
						label: '50%',
					},
					{
						value: 75,
						label: '75%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
			case '50': {
				return [
					{
						value: 0,
						label: '0%',
					},
					{
						value: 50,
						label: '50%',
					},
					{
						value: 100,
						label: '100%',
					},
				];
			}
		}
	};

	const handleSliderChange = (_event: Event, newValue: number | number[]) => {
		if (typeof newValue === 'number') {
			onSliderChanged?.(newValue);
		}
	};

	const handleSliderChangeCommitted = (
		_event: React.SyntheticEvent | Event,
		newValue: number | number[],
	) => {
		if (typeof newValue === 'number') {
			value = newValue;
			onSliderChanged?.(newValue);
		}
	};

	const handleBlur = () => {
		if (value < 0) {
			onSliderChanged?.(0);
		} else if (value > 100) {
			onSliderChanged?.(100);
		}
	};

	const getTheme = () => {
		if (disabled) return DisabledSlider;
		else if (invalid) return InvalidSlider;
		else return CustomSlider;
	};

	return (
		<Grid container alignItems="center" alignContent="center" wrap="nowrap">
			<Grid
				item
				xs={11}
				style={{
					paddingLeft: padLeft ? padLeft : 0,
					paddingRight: '30px',
				}}>
				<ThemeProvider theme={getTheme()}>
					<Slider
						data-cy={dataCy}
						valueLabelDisplay={valueLabelDisplay}
						value={value}
						disabled={disabled}
						onChange={handleSliderChange}
						onChangeCommitted={handleSliderChangeCommitted}
						min={0}
						max={100}
						marks={getMarks(marks)}
						step={1}
					/>
				</ThemeProvider>
			</Grid>
			<Grid item xs={1} style={{ minWidth: '85px' }}>
				<Input
					style={{ width: '85px' }}
					value={value}
					disabled={disabled}
					margin="dense"
					onChange={e => {
						onSliderChanged?.(
							e.target.value === '' ? 0 : Number(e.target.value),
						);
					}}
					onBlur={handleBlur}
					inputProps={{
						step: 1,
						min: 0,
						max: 100,
						type: 'number',
						'aria-labelledby': 'input-slider',
					}}
					endAdornment={'%'}
				/>
			</Grid>
		</Grid>
	);
};
